import { computed, inject, Injectable } from '@angular/core';
import {
  App,
  CreateKnowledgeSourceRequestInterface,
  DeleteKnowledgeSourceRequestInterface,
  DismissKnowledgeSourceTrainingErrorRequestInterface,
  KnowledgeBaseInterface,
  KnowledgeBaseService,
  KnowledgeSource,
  KnowledgeSourceConfig,
  KnowledgeSourceConfigInterface,
  KnowledgeSourceConfigType,
  KnowledgeSourceInterface,
  ListAllAppsForKnowledgeSourceRequestInterface,
  ListAllKnowledgeSourcesForAppRequestInterface,
  ListKnowledgeSourcesRequestInterface,
  ListKnowledgeSourcesResponse,
  NamespaceInterface,
  PagedRequestOptionsInterface,
  ScrapeWebsiteRequestOptions,
  TrainingProgress,
  UpdateKnowledgeBaseRequestInterface,
  UpdateKnowledgeSourceRequest,
  UpsertKnowledgeForAppRequestInterface,
  WebsiteScrapeConfigCrawlMode,
  WebsiteScrapeConfigProgress,
} from '@vendasta/embeddings';
import { catchError, firstValueFrom, switchMap, throwError } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  BUSINESS_PROFILE_URL_TOKEN,
  MANAGE_KNOWLEDGE_URL_TOKEN,
  MARKET_ID_TOKEN,
  PARTNER_ID_TOKEN,
  WEB_CHAT_WIDGET_EDIT_ROUTE_TOKEN,
} from '../tokens';
import { TranslateService } from '@ngx-translate/core';
import { RouteCommand } from '../interface/config.interface';
import dayjs from 'dayjs';
import UTC from 'dayjs/plugin/utc';
import { SetAppsForKnowledgeSourceRequestInterface } from '@vendasta/embeddings';
import { KnowledgeSourceWithSuggestedFlag, WebChatWidgetAppType } from '../ai-knowledge-util';

dayjs.extend(UTC);

export const VENDASTA_PARTNER_ID = 'VA';

@Injectable()
export class AiKnowledgeService {
  private readonly knowledgeBaseService = inject(KnowledgeBaseService);
  private readonly translateService = inject(TranslateService);

  readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));
  readonly marketId = toSignal(inject(MARKET_ID_TOKEN));
  readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly manageKnowledgeUrl = toSignal(inject(MANAGE_KNOWLEDGE_URL_TOKEN));
  private readonly businessProfileUrl = toSignal(inject(BUSINESS_PROFILE_URL_TOKEN));
  private readonly webChatWidgetEditRoute = toSignal(inject(WEB_CHAT_WIDGET_EDIT_ROUTE_TOKEN));
  readonly businessProfileKSId = computed(() => 'KS-business-profile-' + this.accountGroupId());

  async getApp(id: string, includeKnowledgeBaseKeys?: boolean): Promise<App> {
    return await firstValueFrom(
      this.knowledgeBaseService.getApp({
        id: id,
        namespace: this.buildNamespaceInterface(),
        includeKnowledgeBaseKeys: includeKnowledgeBaseKeys,
      }),
    );
  }

  async listAllApps(includeKnowledgeBaseKeys?: boolean): Promise<App[]> {
    let cursor = '';
    let hasMore = true;
    const apps: App[] = [];
    while (hasMore) {
      const response = await firstValueFrom(
        this.knowledgeBaseService.listApps({
          filters: {
            namespace: this.buildNamespaceInterface(),
          },
          pagingOptions: {
            cursor: cursor,
            pageSize: 100,
          },
          includeKnowledgeBaseKeys: includeKnowledgeBaseKeys,
        }),
      );

      cursor = response.metadata.nextCursor;
      hasMore = response.metadata.hasMore;

      apps.push(...(response.apps || []));
    }

    return await this.filterAIAssistantsInApps(apps);
  }

  private async filterAIAssistantsInApps(apps: App[]): Promise<App[]> {
    return apps.filter((app) => app.appType != WebChatWidgetAppType);
  }

  async getKnowledgeSource(
    id: string,
    namespace: NamespaceInterface,
    includeKnowledgeBaseKeys?: boolean,
  ): Promise<KnowledgeSource> {
    return await firstValueFrom(
      this.knowledgeBaseService.getKnowledgeSource({
        id: id,
        namespace: namespace,
        includeKnowledgeBaseKeys: includeKnowledgeBaseKeys,
      }),
    );
  }

  async listKnowledgeSources(
    configTypes?: KnowledgeSourceConfigType[],
    pagingOptions?: PagedRequestOptionsInterface,
  ): Promise<ListKnowledgeSourcesResponse> {
    const request: ListKnowledgeSourcesRequestInterface = {
      filters: {
        namespace: this.buildNamespaceInterface(),
        configTypes: configTypes,
      },
      pagingOptions: pagingOptions,
    };
    return await firstValueFrom(this.knowledgeBaseService.listKnowledgeSources(request));
  }

  async listAllKnowledgeSources(configTypes?: KnowledgeSourceConfigType[]): Promise<KnowledgeSource[]> {
    let cursor = '';
    let hasMore = true;
    const knowledgeSources: KnowledgeSource[] = [];
    while (hasMore) {
      const response = await firstValueFrom(
        this.knowledgeBaseService.listKnowledgeSources({
          filters: {
            namespace: this.buildNamespaceInterface(),
            configTypes: configTypes,
          },
          pagingOptions: {
            cursor: cursor,
            pageSize: 100,
          },
        }),
      );

      cursor = response.metadata.nextCursor;
      hasMore = response.metadata.hasMore;

      knowledgeSources.push(...(response.knowledgeSources || []));
    }

    return knowledgeSources;
  }

  async updateKnowledgeSourceNameAndConfig(source: KnowledgeSource): Promise<KnowledgeSource> {
    const request = new UpdateKnowledgeSourceRequest({
      knowledgeSource: source,
      fieldMask: {
        paths: ['name', 'description', 'config'],
      },
    });
    return firstValueFrom(
      this.knowledgeBaseService.updateKnowledgeSource(request).pipe(
        switchMap(() => {
          return this.knowledgeBaseService.getKnowledgeSource({
            id: source.id,
            namespace: source.namespace,
          });
        }),
      ),
    );
  }

  async createKnowledgeSource(name: string, config: KnowledgeSourceConfigInterface): Promise<string> {
    const request: CreateKnowledgeSourceRequestInterface = {
      knowledgeSource: {
        namespace: this.buildNamespaceInterface(),
        name: name,
        config: config,
      },
    };
    const result = await firstValueFrom(this.knowledgeBaseService.createKnowledgeSource(request));
    return result.id;
  }

  async createKnowledgeSourceWithDescription(
    name: string,
    description: string,
    config: KnowledgeSourceConfigInterface,
  ): Promise<string> {
    const request: CreateKnowledgeSourceRequestInterface = {
      knowledgeSource: {
        namespace: this.buildNamespaceInterface(),
        name: name,
        description: description,
        config: config,
      },
    };
    const result = await firstValueFrom(this.knowledgeBaseService.createKnowledgeSource(request));
    return result.id;
  }

  async createOrUpdateKnowledgeSource(
    knowledgeSource: KnowledgeSource,
    name: string,
    config: KnowledgeSourceConfigInterface,
  ): Promise<string> {
    if (knowledgeSource) {
      const source = knowledgeSource;
      source.name = name;
      source.config = new KnowledgeSourceConfig(config);
      await this.updateKnowledgeSourceNameAndConfig(source);
      return source.id;
    } else {
      return await this.createKnowledgeSource(name, config);
    }
  }

  async deleteKnowledgeSource(id: string): Promise<void> {
    const request: DeleteKnowledgeSourceRequestInterface = {
      id: id,
      namespace: this.buildNamespaceInterface(),
    };
    await firstValueFrom(this.knowledgeBaseService.deleteKnowledgeSource(request));
  }

  async listAllAppsForKnowledgeSource(knowledgeSourceId: string): Promise<App[]> {
    const request: ListAllAppsForKnowledgeSourceRequestInterface = {
      knowledgeSourceId: knowledgeSourceId,
      namespace: this.buildNamespaceInterface(),
    };
    const results = await firstValueFrom(this.knowledgeBaseService.listAllAppsForKnowledgeSource(request));
    return await this.filterAIAssistantsInApps(results.apps || []);
  }

  async listAllKnowledgeSourcesForApp(
    appId: string,
    configTypes?: KnowledgeSourceConfigType[],
  ): Promise<KnowledgeSource[]> {
    const request: ListAllKnowledgeSourcesForAppRequestInterface = {
      appId: appId,
      namespace: this.buildNamespaceInterface(),
      filters: {
        configTypes: configTypes,
      },
    };
    const results = await firstValueFrom(this.knowledgeBaseService.listAllKnowledgeSourcesForApp(request));
    return results.knowledgeSources || [];
  }

  async upsertKnowledgeForApp(
    appId: string,
    name: string,
    configurationUrl: string,
    appType: string,
    knowledgeSources?: KnowledgeSourceInterface[],
  ): Promise<void> {
    const knowledgeSourceKeys = [];

    for (const source of knowledgeSources) {
      knowledgeSourceKeys.push({
        knowledgeSourceId: source.id,
        namespace: source.namespace,
      });
    }

    const request: UpsertKnowledgeForAppRequestInterface = {
      app: {
        id: appId,
        namespace: this.buildNamespaceInterface(),
        name: name,
        configurationUrl: configurationUrl,
        appType: appType,
      },
      knowledgeSourceKeys: knowledgeSourceKeys,
    };
    await firstValueFrom(
      this.knowledgeBaseService.upsertKnowledgeForApp(request).pipe(catchError((err) => throwError(() => err))),
    );
  }

  async setAppsForKnowledgeSource(knowledgeSourceId: string, apps: App[]): Promise<void> {
    const appKeys = [];

    for (const app of apps) {
      appKeys.push({
        appId: app.id,
        namespace: app.namespace,
      });
    }

    const request: SetAppsForKnowledgeSourceRequestInterface = {
      knowledgeSourceKey: {
        knowledgeSourceId: knowledgeSourceId,
        namespace: this.buildNamespaceInterface(),
      },
      appKeys: appKeys,
    };
    await firstValueFrom(
      this.knowledgeBaseService.setAppsForKnowledgeSource(request).pipe(catchError((err) => throwError(() => err))),
    );
  }

  buildNamespaceInterface(): NamespaceInterface {
    if (this.accountGroupId()) {
      return {
        accountGroupNamespace: {
          accountGroupId: this.accountGroupId(),
        },
      };
    }
    if (this.partnerId() && this.partnerId() !== VENDASTA_PARTNER_ID) {
      return {
        partnerNamespace: {
          partnerId: this.partnerId(),
        },
      };
    }
    return {
      adminNamespace: {},
    };
  }

  buildManageKnowledgeUrl(): string {
    return this.manageKnowledgeUrl();
  }

  buildBusinessProfileUrl(): string {
    return this.businessProfileUrl();
  }

  isWebChatCreateRouteSet(): boolean {
    return this.webChatWidgetEditRoute()?.length > 0;
  }

  buildWebChatWidgetEditRoute(widgetId: string): RouteCommand[] {
    return this.webChatWidgetEditRoute().map((segment) => {
      if (typeof segment === 'string') {
        return segment === ':id' ? widgetId : segment;
      }
      if (segment['outlets']) {
        for (const [key, value] of Object.entries(segment['outlets'])) {
          if (typeof value === 'string') {
            segment['outlets'][key] = value === ':id' ? widgetId : value;
          } else {
            const valueArray = value as string[];
            segment['outlets'][key] = valueArray.map((item) => (item === ':id' ? widgetId : item));
          }
        }
      }
      return segment;
    });
  }

  buildBusinessProfileKnowledgeSourceId(): string {
    if (this.accountGroupId()) {
      return `KS-business-profile-${this.accountGroupId()}`;
    }
    return '';
  }

  buildBusinessProfileKnowledgeSource(shouldSuggest = false): KnowledgeSourceWithSuggestedFlag | null {
    if (this.accountGroupId()) {
      return {
        id: this.buildBusinessProfileKnowledgeSourceId(),
        namespace: this.buildNamespaceInterface(),
        name: 'Business Profile', // untranslated because that is how the name is in the backend
        config: {
          businessProfileConfig: {
            accountGroupId: this.accountGroupId(),
          },
        },
        isSuggested: shouldSuggest,
        training: {
          progress: TrainingProgress.TRAINING_DONE,
        },
      } as KnowledgeSourceWithSuggestedFlag;
    }
    return null;
  }

  buildBusinessWebsiteKnowledgeSourceId(): string {
    if (this.accountGroupId()) {
      return `KS-business-website-${this.accountGroupId()}`;
    }
    return '';
  }

  buildBusinessWebsiteKnowledgeSource(url: string, shouldSuggest = false): KnowledgeSourceWithSuggestedFlag | null {
    if (url) {
      return {
        id: this.buildBusinessWebsiteKnowledgeSourceId(),
        namespace: this.buildNamespaceInterface(),
        name: 'Business Website', // untranslated because that is how the name is in the backend
        config: {
          websiteScrapeConfig: {
            url: url,
            crawlMode: WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_SINGLE,
            scrapeProgress: WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_QUEUED,
          },
        },
        isSuggested: shouldSuggest,
      } as KnowledgeSourceWithSuggestedFlag;
    }
    return null;
  }

  getKnowledgePreview(source: KnowledgeSource): string {
    if (source?.config?.businessProfileConfig?.accountGroupId) {
      return this.translateService.instant('INBOX.WEBCHAT.SETTINGS.BUSINESS_PROFILE.DESCRIPTION');
    }
    if (source?.config?.customDataConfig) {
      return source?.config?.customDataConfig?.text;
    }
    if (source?.config?.websiteScrapeConfig) {
      return source?.config?.websiteScrapeConfig?.url;
    }
    if (source?.config?.fileConfig) {
      // Use fileName if available, fallback to description, then to mimetype
      const fileName =
        source?.config?.fileConfig?.fileName ||
        source?.config?.fileConfig?.queuedFile?.fileName ||
        source?.config?.fileConfig?.erroredFile?.fileName;
      if (fileName) {
        return fileName;
      }
      const mimetype =
        source?.config?.fileConfig?.mimetype ||
        source?.config?.fileConfig?.queuedFile?.mimetype ||
        source?.config?.fileConfig?.erroredFile?.mimetype;
      return source?.description || `File: ${mimetype || 'Unknown type'}`;
    }

    return '';
  }

  getLastScraped(source: KnowledgeSource): string {
    const lastScraped = source?.config?.websiteScrapeConfig?.lastScraped;
    if (!lastScraped) {
      return 'unknown';
    }

    return dayjs.utc(lastScraped).format('MMM D, YYYY');
  }

  async rescrapeWebsite(source: KnowledgeSource, options?: ScrapeWebsiteRequestOptions): Promise<void> {
    await firstValueFrom(
      this.knowledgeBaseService.scrapeWebsite({
        knowledgeSourceId: source.id,
        namespace: source.namespace,
        options: options,
      }),
    );
  }

  getAppDisplayName(app: App): string {
    if (app.appTypeName) {
      return app.name + ' • ' + app.appTypeName;
    }
    return app.name;
  }

  async getOtherAIDisclaimerText(knowledgeSourceId: string, currentAppId: string): Promise<string> {
    if (knowledgeSourceId && currentAppId) {
      const apps = await this.listAllAppsForKnowledgeSource(knowledgeSourceId);
      const filteredApps = apps.filter((app) => currentAppId != app.id);
      if (filteredApps.length > 0) {
        return this.translateService.instant('EDIT_KNOWLEDGE.OTHER_AI', { count: filteredApps.length });
      }
    }
    return '';
  }

  async dismissKnowledgeSourceTrainingError(
    knowledgeSourceId: string,
    namespace?: NamespaceInterface,
  ): Promise<boolean> {
    const request: DismissKnowledgeSourceTrainingErrorRequestInterface = {
      knowledgeSourceKey: {
        knowledgeSourceId,
        namespace: namespace || this.buildNamespaceInterface(),
      },
    };
    return firstValueFrom(this.knowledgeBaseService.dismissKnowledgeSourceTrainingError(request));
  }

  // ─── Knowledge-base CRUD (DALE-1279) ─────────────────────────────────────
  //
  // `@vendasta/embeddings` publicly exposes only `updateKnowledgeBase`; create + list live on the unexported KnowledgeBaseApiService, so they're stubbed until the public surface expands. Consumers code against these signatures — swapping in the real RPC is a one-file change.

  async createKnowledgeBase(
    name: string,
    description: string,
    knowledgeSourceIds: string[],
  ): Promise<{ id: string; namespace: NamespaceInterface }> {
    // STUB(DALE-1279-followup): real call goes through KnowledgeBaseApiService.
    void name;
    void description;
    void knowledgeSourceIds;
    const namespace = this.buildNamespaceInterface();
    const stubId = `kb-stub-${Date.now()}`;
    return { id: stubId, namespace };
  }

  /** Updates a KB's knowledge-source membership via `updateKnowledgeBase` with a `knowledge_source_keys` field mask. */
  async updateKnowledgeBaseSources(
    kbId: string,
    kbNamespace: NamespaceInterface,
    knowledgeSourceIds: string[],
  ): Promise<void> {
    const knowledgeBase: KnowledgeBaseInterface = {
      id: kbId,
      namespace: kbNamespace,
      knowledgeSourceKeys: knowledgeSourceIds.map((knowledgeSourceId) => ({
        knowledgeSourceId,
        namespace: kbNamespace,
      })),
    };
    const request: UpdateKnowledgeBaseRequestInterface = {
      knowledgeBase,
      fieldMask: { paths: ['knowledge_source_keys'] },
    };
    await firstValueFrom(this.knowledgeBaseService.updateKnowledgeBase(request));
  }

  /** Lists KBs visible to the caller. Stubbed to an empty list (rendered as "no KBs") until the public surface exposes `listKnowledgeBases`. */
  async listKnowledgeBases(_includeKnowledgeSourceKeys = true): Promise<KnowledgeBaseInterface[]> {
    // STUB(DALE-1279-followup): real call goes through KnowledgeBaseApiService.
    void _includeKnowledgeSourceKeys;
    return [];
  }
}
